import { PERMISSIONS, type UserPermission } from '@/models/enums/permissions'
import { UserRole, toUserRole } from '@/models/user'

export type VeePermission = (typeof PERMISSIONS.VEE)[keyof typeof PERMISSIONS.VEE]

const VEE_PERMISSIONS = new Set<VeePermission>(Object.values(PERMISSIONS.VEE))

const ALLOWED_ROLES: Record<VeePermission, readonly UserRole[]> = {
  [PERMISSIONS.VEE.GROUPS_VIEW]: [
    UserRole.User,
    UserRole.Admin,
    UserRole.Operator,
    UserRole.Sap,
    UserRole.Manager,
    UserRole.Monitor,
    UserRole.Root,
  ],
  [PERMISSIONS.VEE.GROUPS_MANAGE]: [UserRole.Admin, UserRole.Sap, UserRole.Manager],
  [PERMISSIONS.VEE.GROUPS_ADMINISTER]: [UserRole.Admin, UserRole.Root],
  [PERMISSIONS.VEE.RULES_VIEW]: [
    UserRole.User,
    UserRole.Admin,
    UserRole.Operator,
    UserRole.Sap,
    UserRole.Manager,
    UserRole.Monitor,
    UserRole.Root,
  ],
  [PERMISSIONS.VEE.RULES_MANAGE]: [
    UserRole.Operator,
    UserRole.Admin,
    UserRole.Sap,
    UserRole.Monitor,
    UserRole.Manager,
    UserRole.Root,
  ],
  [PERMISSIONS.VEE.RULES_ADMINISTER]: [UserRole.Admin, UserRole.Root],
  [PERMISSIONS.VEE.SERVICE_REQUESTS_VIEW]: [
    UserRole.User,
    UserRole.Admin,
    UserRole.Operator,
    UserRole.Sap,
    UserRole.Manager,
    UserRole.Monitor,
    UserRole.Root,
  ],
  [PERMISSIONS.VEE.SERVICE_REQUESTS_MANAGE]: [
    UserRole.Operator,
    UserRole.Admin,
    UserRole.Sap,
    UserRole.Manager,
  ],
  [PERMISSIONS.VEE.MANUAL_OVERRIDE_EDIT]: [
    UserRole.Operator,
    UserRole.Admin,
    UserRole.Sap,
    UserRole.Manager,
  ],
  [PERMISSIONS.VEE.BILLING_READINESS_VIEW]: [
    UserRole.User,
    UserRole.Admin,
    UserRole.Operator,
    UserRole.Sap,
    UserRole.Manager,
    UserRole.Monitor,
    UserRole.Root,
  ],
  [PERMISSIONS.VEE.BILLING_READINESS_RELEASE]: [UserRole.Admin, UserRole.Sap, UserRole.Manager],
  [PERMISSIONS.VEE.EXCEPTIONS_MANAGE]: [UserRole.Admin, UserRole.Sap, UserRole.Manager],
  [PERMISSIONS.VEE.ESTIMATION_PROPOSALS_VIEW]: [
    UserRole.User,
    UserRole.Admin,
    UserRole.Operator,
    UserRole.Sap,
    UserRole.Manager,
    UserRole.Monitor,
    UserRole.Root,
  ],
  [PERMISSIONS.VEE.ESTIMATION_PROPOSALS_DECIDE]: [
    UserRole.Operator,
    UserRole.Admin,
    UserRole.Sap,
    UserRole.Manager,
  ],
  [PERMISSIONS.VEE.ESTIMATION_PROPOSALS_BATCH_DECIDE]: [
    UserRole.Admin,
    UserRole.Sap,
    UserRole.Manager,
  ],
  [PERMISSIONS.VEE.NOTIFICATIONS_VIEW]: [
    UserRole.User,
    UserRole.Admin,
    UserRole.Operator,
    UserRole.Sap,
    UserRole.Manager,
    UserRole.Monitor,
    UserRole.Root,
  ],
  [PERMISSIONS.VEE.NOTIFICATIONS_MANAGE]: [UserRole.Admin, UserRole.Sap, UserRole.Manager],
  [PERMISSIONS.VEE.EXECUTION_MONITOR_VIEW]: [
    UserRole.User,
    UserRole.Admin,
    UserRole.Operator,
    UserRole.Sap,
    UserRole.Manager,
    UserRole.Monitor,
    UserRole.Root,
  ],
}

const getStoredUserRole = (): UserRole | null => {
  if (typeof localStorage === 'undefined') return null

  try {
    const storedUser = localStorage.getItem('user')
    if (!storedUser) return null

    return toUserRole(JSON.parse(storedUser)?.userRole)
  } catch {
    return null
  }
}

export const isVeePermission = (permission: UserPermission): permission is VeePermission =>
  VEE_PERMISSIONS.has(permission as VeePermission)

export const hasVeePermission = (permission: VeePermission): boolean => {
  if (typeof localStorage === 'undefined') return false

  const userRole = getStoredUserRole()
  return userRole !== null && ALLOWED_ROLES[permission].includes(userRole)
}
